import React, { useMemo, useState } from 'react';

import { Button, IconShare, Tag, useTheme } from '@veneer/core';

import { ShareInfoItemsContainer, ShareInfoWrapper } from '../styled';

import { useDependencyManagerContext } from '../../../contexts/dependencyManager';
import { Device } from '../../../types/devices';
import ShareDeviceModal from './share-device-modal';

type ShareDeviceProps = {
  'data-testid': string;
  device: Device;
  isShared?: boolean;
};

const ShareDevice: React.FC<ShareDeviceProps> = (props) => {
  const { 'data-testid': dataTestId, device, isShared } = props;
  const theme = useTheme();
  const { translate } = useDependencyManagerContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const deviceFamily = device.device_family;
  const serialNumber = device.device_serial_nr;

  const unknownDeviceName = translate('components.device-item.no-data.unknown');

  const shareButtonAnalyticsAttributes = useMemo(
    () => ({
      'data-gtm-category': 'linkClick',
      'data-gtm-id': `device-card-share-button-${serialNumber}`,
      'data-gtm-value': `share-device-${deviceFamily}-${serialNumber}`
    }),
    [serialNumber, deviceFamily]
  );

  return (
    <>
      <ShareInfoWrapper $veneerTheme={theme}>
        <ShareInfoItemsContainer $veneerTheme={theme}>
          {isShared && (
            <Tag
              label={translate('components.device-item.shared-tag.label')}
              colorScheme="electricBlue"
              isTint
              aria-label={translate(
                'components.device-item.shared-tag.aria-label',
                {
                  deviceFamily: deviceFamily || unknownDeviceName,
                  serialNumber
                }
              )}
              data-testid={`${dataTestId}-shared-tag`}
            />
          )}
          <Button
            appearance="ghost"
            small
            leadingIcon={<IconShare />}
            aria-label={translate(
              'components.device-item.share-button.aria-label',
              {
                deviceFamily: deviceFamily || unknownDeviceName,
                serialNumber
              }
            )}
            data-testid={dataTestId}
            onClick={() => setIsModalOpen(true)}
            customStyle={{ marginLeft: 'auto' }}
            {...shareButtonAnalyticsAttributes}
          />
        </ShareInfoItemsContainer>
      </ShareInfoWrapper>

      <ShareDeviceModal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        device={device}
        data-testid={`${dataTestId}-modal`}
      />
    </>
  );
};

export default ShareDevice;
